import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { Layout } from '../../components/layout/Layout';
import { AdminHeader } from '../../components/admin/AdminHeader';
import { AdminNav } from '../../components/admin/AdminNav';
import { AdminLoginCard } from '../../components/admin/AdminLoginCard';
import { AbandonedCheckoutsPanel } from '../../components/admin/AbandonedCheckoutsPanel';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { useProducts } from '../../hooks/useProducts';
import { db, functions } from '../../firebase';
import {
  getInTransitMap,
  getOrders,
  getStockMap,
  saveStockMap,
  STOCK_UPDATED_EVENT,
} from '../../utils/adminStorage';

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('hu-HU');
};

const ORDER_STATUSES = [
  { id: 'new', label: 'Új' },
  { id: 'packed', label: 'Összecsomagolva' },
  { id: 'shipped', label: 'Feladva' },
];

const getOrderStatus = (order) => order?.status || 'new';
const getOrderStatusLabel = (statusId) =>
  ORDER_STATUSES.find((status) => status.id === statusId)?.label || statusId;
const ORDERS_STORAGE_KEY = 'lumenverde-orders';
const normalizeLookupKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeShippingKey = (shippingMethod) =>
  String(shippingMethod || '')
    .trim()
    .toLowerCase();

const isPacketaShipping = (shippingMethod) => {
  const key = normalizeShippingKey(shippingMethod);
  return key.includes('packeta') || key.includes('zpoint') || key.includes('z-point');
};

const isPersonalPickupShipping = (shippingMethod) => {
  const key = normalizeShippingKey(shippingMethod);
  return key === 'personal_pickup_omega' || key.includes('personal_pickup') || key.includes('szemelyes');
};

const isGlsHomeShipping = (shippingMethod) => {
  const key = normalizeShippingKey(shippingMethod);
  return key === 'gls_home' || key === 'home' || key === 'standard';
};

const isGlsParcelLockerShipping = (shippingMethod) => {
  const key = normalizeShippingKey(shippingMethod);
  return key === 'gls_parcel_locker' || key === 'express' || key.includes('gls_parcel');
};

const hasPickupPointShipping = (shippingMethod) =>
  isPacketaShipping(shippingMethod) || isGlsParcelLockerShipping(shippingMethod);

const getShippingCarrier = (shippingMethod) => {
  if (isPacketaShipping(shippingMethod)) return 'packeta';
  if (isGlsHomeShipping(shippingMethod) || isGlsParcelLockerShipping(shippingMethod)) return 'gls';
  return 'other';
};

const getShippingMethodLabel = (shippingMethod) => {
  if (isPersonalPickupShipping(shippingMethod)) return 'Személyes átvétel (Omega Sportcentrum)';
  if (isPacketaShipping(shippingMethod)) return 'Packeta csomagpont';
  if (isGlsParcelLockerShipping(shippingMethod)) return 'GLS csomagautomata';
  if (isGlsHomeShipping(shippingMethod)) return 'GLS házhozszállítás';
  return 'GLS házhozszállítás';
};

const getShippingBadgeClassName = (shippingMethod) => {
  if (isPacketaShipping(shippingMethod)) {
    return 'border-emerald-300/80 bg-emerald-50 text-emerald-800';
  }
  if (isGlsParcelLockerShipping(shippingMethod)) {
    return 'border-sky-300/80 bg-sky-50 text-sky-800';
  }
  if (isGlsHomeShipping(shippingMethod)) {
    return 'border-cyan-300/80 bg-cyan-50 text-cyan-800';
  }
  if (isPersonalPickupShipping(shippingMethod)) {
    return 'border-amber-300/80 bg-amber-50 text-amber-800';
  }
  return 'border-border/80 bg-muted/60 text-foreground';
};

const formatPickupPointLabel = (pickupPoint) => {
  if (!pickupPoint || typeof pickupPoint !== 'object') return '';
  const id = typeof pickupPoint.id === 'string' ? pickupPoint.id.trim() : '';
  const name = typeof pickupPoint.name === 'string' ? pickupPoint.name.trim() : '';
  if (name && id) return `${name} (${id})`;
  return name || id;
};

const toMillis = (value) => {
  if (!value) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (typeof value === 'object') {
    if (typeof value.toMillis === 'function') {
      try {
        return value.toMillis();
      } catch {
        return null;
      }
    }
    if (typeof value.seconds === 'number') {
      const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : 0;
      return value.seconds * 1000 + nanos / 1e6;
    }
  }
  return null;
};

const getArrivalTime = (order) =>
  toMillis(order?.createdAtServer) ?? toMillis(order?.createdAt) ?? Number.MAX_SAFE_INTEGER;

const getOrderRefFromOrder = (order) =>
  order?.id ? `id:${order.id}` : `num:${order?.orderNumber || ''}`;

const getOrderRefFromAdjustment = (entry) =>
  entry?.orderId ? `id:${entry.orderId}` : `num:${entry?.orderNumber || ''}`;

export default function AdminOrders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView = searchParams.get('nez') === 'felbehagyott' ? 'abandoned' : 'orders';
  const { user, loading } = useAdminAuth();
  const { products } = useProducts();
  const [orders, setOrders] = useState(() => getOrders());
  const [procurements, setProcurements] = useState([]);
  const [inventoryAdjustments, setInventoryAdjustments] = useState([]);
  const [stockMap, setStockMap] = useState(() => getStockMap());
  const [inTransitMap, setInTransitMap] = useState(() => getInTransitMap());
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [carrierFilter, setCarrierFilter] = useState('all');
  const [updatingOrderKeys, setUpdatingOrderKeys] = useState({});
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState('');

  const handleInvoiceDownload = async (order) => {
    if (!order?.id) return;
    const popup = window.open('', '_blank');
    setDownloadingInvoiceId(order.id);
    try {
      const getInvoiceDownloadUrl = httpsCallable(functions, 'getInvoiceDownloadUrl');
      const result = await getInvoiceDownloadUrl({ orderId: order.id });
      if (!result.data?.url) throw new Error('Missing invoice URL');
      if (popup) popup.location.href = result.data.url;
      else window.location.href = result.data.url;
    } catch {
      if (popup) popup.close();
      setError('Nem sikerült megnyitni a számla PDF-et. Próbáld újra.');
    } finally {
      setDownloadingInvoiceId('');
    }
  };

  useEffect(() => {
    if (!user) return undefined;
    const q = query(collection(db, 'orders'), orderBy('createdAtServer', 'desc'));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const nextOrders = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setOrders(nextOrders.length ? nextOrders : getOrders());
        setError('');
      },
      () => {
        setError('Nem sikerült betölteni a rendelések listáját a Firestore-ból.');
        setOrders(getOrders());
      },
    );
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    const q = query(collection(db, 'purchase'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const allProcurements = snapshot.docs.map((snap) => ({ id: snap.id, ...snap.data() }));
        setProcurements(allProcurements);
        const activeProcurements = allProcurements
          .filter((entry) => !entry?.receivedAt);
        const totals = {};
        activeProcurements.forEach((procurement) => {
          const items = Array.isArray(procurement.items) ? procurement.items : [];
          items.forEach((item) => {
            if (!item?.productId) return;
            const qty = Math.max(1, Number(item.qty) || 1);
            totals[item.productId] = (Number(totals[item.productId]) || 0) + qty;
          });
        });
        setInTransitMap(totals);
      },
      () => {
        setInTransitMap(getInTransitMap());
      },
    );
    return () => unsub();
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    const q = query(collection(db, 'inventoryAdjustments'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const next = snapshot.docs.map((snap) => ({ id: snap.id, ...snap.data() }));
        setInventoryAdjustments(next);
      },
      () => {
        setInventoryAdjustments([]);
      },
    );
    return () => unsub();
  }, [user]);

  useEffect(() => {
    setStockMap(getStockMap());
  }, [user]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const syncStock = (event) => {
      if (
        !event ||
        event.type === STOCK_UPDATED_EVENT ||
        event.key === null ||
        event.key === 'lumenverde-stock'
      ) {
        setStockMap(getStockMap());
      }
    };
    window.addEventListener(STOCK_UPDATED_EVENT, syncStock);
    window.addEventListener('storage', syncStock);
    window.addEventListener('focus', syncStock);
    return () => {
      window.removeEventListener(STOCK_UPDATED_EVENT, syncStock);
      window.removeEventListener('storage', syncStock);
      window.removeEventListener('focus', syncStock);
    };
  }, []);

  const productIdByNormalizedName = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      if (!product?.id || !product?.name) return;
      const lookup = normalizeLookupKey(product.name);
      if (lookup && !map.has(lookup)) {
        map.set(lookup, product.id);
      }
    });
    return map;
  }, [products]);

  const productNameById = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      if (!product?.id) return;
      map.set(product.id, product.name || product.id);
    });
    return map;
  }, [products]);

  const sortedOrders = useMemo(
    () => [...orders].sort((a, b) => getArrivalTime(a) - getArrivalTime(b)),
    [orders],
  );

  const statusCounts = useMemo(() => {
    const counts = ORDER_STATUSES.reduce((acc, status) => {
      acc[status.id] = 0;
      return acc;
    }, {});
    sortedOrders.forEach((order) => {
      const status = getOrderStatus(order);
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [sortedOrders]);

  const carrierCounts = useMemo(() => {
    const counts = {
      all: sortedOrders.length,
      gls: 0,
      packeta: 0,
    };
    sortedOrders.forEach((order) => {
      const carrier = getShippingCarrier(order.shipping);
      if (carrier === 'gls') counts.gls += 1;
      if (carrier === 'packeta') counts.packeta += 1;
    });
    return counts;
  }, [sortedOrders]);

  const filteredOrders = useMemo(() => {
    return sortedOrders.filter((order) => {
      const statusMatches = statusFilter === 'all' || getOrderStatus(order) === statusFilter;
      const carrierMatches = carrierFilter === 'all' || getShippingCarrier(order.shipping) === carrierFilter;
      return statusMatches && carrierMatches;
    });
  }, [sortedOrders, statusFilter, carrierFilter]);

  const newOrders = useMemo(
    () => sortedOrders.filter((order) => getOrderStatus(order) === 'new'),
    [sortedOrders],
  );

  const aggregatedRequiredProducts = useMemo(() => {
    const aggregated = new Map();

    newOrders.forEach((order) => {
      const countedInOrder = new Set();
      (order.items || []).forEach((item) => {
        const name = typeof item?.name === 'string' ? item.name.trim() : '';
        const quantity = Number(item?.quantity);
        const price = Number(item?.price);
        if (!name || !Number.isFinite(quantity) || quantity <= 0) return;

        const normalizedPrice = Number.isFinite(price) ? price : null;
        const normalizedName = normalizeLookupKey(name);
        const productId = productIdByNormalizedName.get(normalizedName) || null;
        const key = productId || normalizedName;
        if (!aggregated.has(key)) {
          aggregated.set(key, {
            key,
            name,
            productId,
            quantity: 0,
            orders: 0,
            unitPrice: normalizedPrice,
          });
        }

        const existing = aggregated.get(key);
        existing.quantity += quantity;
        if (!countedInOrder.has(key)) {
          existing.orders += 1;
          countedInOrder.add(key);
        }
      });
    });

    return [...aggregated.values()]
      .map((item) => {
        const stockQty = item.productId ? Number(stockMap[item.productId]) || 0 : 0;
        const inTransitQty = item.productId ? Number(inTransitMap[item.productId]) || 0 : 0;

        let availability = 'ok';
        if (item.quantity > stockQty + inTransitQty) {
          availability = 'missing_stock';
        } else if (item.quantity > stockQty) {
          availability = 'warning';
        }

        return {
          ...item,
          stockQty,
          inTransitQty,
          availability,
        };
      })
      .sort((a, b) => b.quantity - a.quantity || a.name.localeCompare(b.name, 'hu-HU'));
  }, [inTransitMap, newOrders, productIdByNormalizedName, stockMap]);

  const aggregatedQuantityTotal = useMemo(
    () => aggregatedRequiredProducts.reduce((total, item) => total + item.quantity, 0),
    [aggregatedRequiredProducts],
  );

  const getOrderRequirements = (order) => {
    const requirements = {};
    const unresolvedItemNames = new Set();
    (order.items || []).forEach((item) => {
      const itemName = typeof item?.name === 'string' ? item.name.trim() : '';
      const quantity = Number(item?.quantity);
      if (!itemName || !Number.isFinite(quantity) || quantity <= 0) return;

      const productId = productIdByNormalizedName.get(normalizeLookupKey(itemName));
      if (!productId) {
        unresolvedItemNames.add(itemName);
        return;
      }

      requirements[productId] = (Number(requirements[productId]) || 0) + quantity;
    });
    return { requirements, unresolvedItemNames: Array.from(unresolvedItemNames) };
  };

  const createStockAdjustments = (order, direction) => {
    const { requirements, unresolvedItemNames } = getOrderRequirements(order);
    const adjustments = Object.fromEntries(
      Object.entries(requirements).map(([productId, qty]) => [productId, direction * qty]),
    );
    return { adjustments, unresolvedItemNames };
  };

  const getPackValidation = (order) => {
    const { requirements, unresolvedItemNames } = getOrderRequirements(order);
    const insufficientProducts = [];

    Object.entries(requirements).forEach(([productId, requiredQty]) => {
      const normalizedRequiredQty = Number(requiredQty) || 0;
      if (!normalizedRequiredQty) return;
      const availableQty = Number(stockMap[productId]) || 0;
      // Packed státusz csak akkor engedett, ha a raktárkészlet legalább akkora
      // az adott rendelés adott termék darabszámánál.
      if (availableQty < normalizedRequiredQty) {
        const productLabel = productNameById.get(productId) || productId;
        insufficientProducts.push(`${productLabel} (${availableQty}/${normalizedRequiredQty})`);
      }
    });

    return {
      canPack: unresolvedItemNames.length === 0 && insufficientProducts.length === 0,
      unresolvedItemNames,
      insufficientProducts,
    };
  };

  const receivedTotalsByProductId = useMemo(() => {
    const map = new Map();
    procurements.forEach((procurement) => {
      if (!procurement?.receivedAt) return;
      const items = Array.isArray(procurement?.items) ? procurement.items : [];
      items.forEach((item) => {
        const productId = item?.productId;
        if (!productId) return;
        const total =
          Number(item?.totalPrice)
          || Number(item?.linePrice)
          || Number(item?.price)
          || ((Number(item?.unitPrice) || 0) * Math.max(1, Number(item?.qty) || 1));
        if (!Number.isFinite(total) || total < 0) return;
        map.set(productId, (map.get(productId) || 0) + total);
      });
    });
    return map;
  }, [procurements]);

  const totalsFromAdjustmentsByType = useMemo(() => {
    const totals = {
      scrap: new Map(),
      orderPack: new Map(),
      orderUnpack: new Map(),
    };
    inventoryAdjustments.forEach((entry) => {
      const productId = entry?.productId;
      if (!productId) return;
      const amount = Number(entry?.amount);
      if (!Number.isFinite(amount) || amount < 0) return;

      if (entry?.type === 'scrap') {
        totals.scrap.set(productId, (totals.scrap.get(productId) || 0) + amount);
      } else if (entry?.type === 'order_pack') {
        totals.orderPack.set(productId, (totals.orderPack.get(productId) || 0) + amount);
      } else if (entry?.type === 'order_unpack') {
        totals.orderUnpack.set(productId, (totals.orderUnpack.get(productId) || 0) + amount);
      }
    });
    return totals;
  }, [inventoryAdjustments]);

  const inventoryValueByProduct = useMemo(() => {
    const next = {};
    const ids = new Set([
      ...receivedTotalsByProductId.keys(),
      ...totalsFromAdjustmentsByType.scrap.keys(),
      ...totalsFromAdjustmentsByType.orderPack.keys(),
      ...totalsFromAdjustmentsByType.orderUnpack.keys(),
    ]);
    ids.forEach((productId) => {
      const received = Number(receivedTotalsByProductId.get(productId) || 0);
      const scrap = Number(totalsFromAdjustmentsByType.scrap.get(productId) || 0);
      const packed = Number(totalsFromAdjustmentsByType.orderPack.get(productId) || 0);
      const unpacked = Number(totalsFromAdjustmentsByType.orderUnpack.get(productId) || 0);
      next[productId] = Math.max(0, received - scrap - packed + unpacked);
    });
    return next;
  }, [receivedTotalsByProductId, totalsFromAdjustmentsByType]);

  const orderValueBalanceByProduct = useMemo(() => {
    const map = new Map();
    inventoryAdjustments.forEach((entry) => {
      if (entry?.type !== 'order_pack' && entry?.type !== 'order_unpack') return;
      const orderRef = getOrderRefFromAdjustment(entry);
      const productId = entry?.productId;
      const amount = Number(entry?.amount);
      if (!orderRef || !productId) return;
      if (!Number.isFinite(amount) || amount < 0) return;
      const key = `${orderRef}::${productId}`;
      const current = map.get(key) || { packed: 0, unpacked: 0 };
      if (entry.type === 'order_pack') current.packed += amount;
      if (entry.type === 'order_unpack') current.unpacked += amount;
      map.set(key, current);
    });
    return map;
  }, [inventoryAdjustments]);

  const createInventoryValueAdjustments = (order, direction) => {
    const { requirements, unresolvedItemNames } = getOrderRequirements(order);
    const type = direction === -1 ? 'order_pack' : 'order_unpack';
    const orderRef = getOrderRefFromOrder(order);
    const entries = Object.entries(requirements)
      .map(([productId, qty]) => {
        const normalizedQty = Math.max(0, Number(qty) || 0);
        if (!normalizedQty) return null;
        let amount = 0;
        let unitPrice = 0;

        if (direction === -1) {
          const currentStockQty = Math.max(0, Number(stockMap[productId]) || 0);
          const currentInventoryValue = Math.max(0, Number(inventoryValueByProduct[productId]) || 0);
          unitPrice = currentStockQty > 0 ? currentInventoryValue / currentStockQty : 0;
          amount = Math.max(0, unitPrice * normalizedQty);
        } else {
          const key = `${orderRef}::${productId}`;
          const balance = orderValueBalanceByProduct.get(key) || { packed: 0, unpacked: 0 };
          const outstandingPackedAmount = Math.max(0, balance.packed - balance.unpacked);
          amount = outstandingPackedAmount;
          unitPrice = normalizedQty > 0 ? amount / normalizedQty : 0;
        }

        return {
          type,
          productId,
          productName: productNameById.get(productId) || productId,
          qty: normalizedQty,
          unitPrice,
          amount,
          orderId: order?.id || null,
          orderNumber: order?.orderNumber || null,
        };
      })
      .filter((entry) => entry && entry.amount > 0);
    return { entries, unresolvedItemNames };
  };

  const applyStockAdjustments = (adjustments) => {
    if (!adjustments || !Object.keys(adjustments).length) return;
    setStockMap((prev) => {
      const nextStock = { ...prev };
      Object.entries(adjustments).forEach(([productId, delta]) => {
        const currentQty = Number(nextStock[productId]) || 0;
        nextStock[productId] = Math.max(0, currentQty + delta);
      });
      saveStockMap(nextStock);
      return nextStock;
    });
  };

  const reverseAdjustments = (adjustments) => {
    if (!adjustments) return null;
    return Object.fromEntries(
      Object.entries(adjustments).map(([productId, delta]) => [productId, -Number(delta || 0)]),
    );
  };

  const updateOrderStatusInState = (targetOrder, nextStatus) => {
    setOrders((prev) =>
      prev.map((item) => {
        const isSameOrder = targetOrder.id
          ? item.id === targetOrder.id
          : item.orderNumber === targetOrder.orderNumber;
        return isSameOrder ? { ...item, status: nextStatus } : item;
      }),
    );
  };

  const updateOrderStatusInLocalStorage = (targetOrder, nextStatus) => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const currentOrders = getOrders();
    const nextOrders = currentOrders.map((item) =>
      item.orderNumber === targetOrder.orderNumber ? { ...item, status: nextStatus } : item,
    );
    window.localStorage.setItem(ORDERS_STORAGE_KEY, JSON.stringify(nextOrders));
    window.dispatchEvent(new Event('lumenverde-orders-updated'));
  };

  const handleStatusChange = async (order, nextStatus) => {
    const orderKey = order.id || order.orderNumber;
    if (!orderKey || updatingOrderKeys[orderKey]) return;

    const currentStatus = getOrderStatus(order);
    if (!nextStatus || nextStatus === currentStatus) return;

    if (currentStatus === 'new' && nextStatus === 'packed') {
      const { canPack, unresolvedItemNames, insufficientProducts } = getPackValidation(order);
      if (!canPack) {
        const messageParts = [];
        if (insufficientProducts.length) {
          messageParts.push(
            `Nincs elég készlet az összecsomagoláshoz (raktár/rendelés): ${insufficientProducts.join(', ')}`,
          );
        }
        if (unresolvedItemNames.length) {
          messageParts.push(
            `Nem azonosítható rendelési tétel: ${unresolvedItemNames.join(', ')}`,
          );
        }
        setError(messageParts.join(' | '));
        return;
      }
    }

    setUpdatingOrderKeys((prev) => ({ ...prev, [orderKey]: true }));

    try {
      const transitionDirection =
        currentStatus === 'new' && nextStatus === 'packed'
          ? -1
          : currentStatus === 'packed' && nextStatus === 'new'
            ? 1
            : 0;
      const stockAdjustmentResult =
        transitionDirection !== 0
          ? createStockAdjustments(order, transitionDirection)
          : { adjustments: null, unresolvedItemNames: [] };
      const valueAdjustmentResult =
        transitionDirection !== 0
          ? createInventoryValueAdjustments(order, transitionDirection)
          : { entries: [], unresolvedItemNames: [] };
      const adjustments = stockAdjustmentResult.adjustments;
      const unresolvedItemNames = Array.from(
        new Set([
          ...stockAdjustmentResult.unresolvedItemNames,
          ...valueAdjustmentResult.unresolvedItemNames,
        ]),
      );

      if (!order.id) {
        updateOrderStatusInState(order, nextStatus);
        updateOrderStatusInLocalStorage(order, nextStatus);
        applyStockAdjustments(adjustments);
        if (unresolvedItemNames.length) {
          setError(
            `A státusz frissült, de ezekhez a tételekhez nem találtam raktár azonosítót: ${unresolvedItemNames.join(', ')}`,
          );
        } else {
          setError('');
        }
        return;
      }

      updateOrderStatusInState(order, nextStatus);
      applyStockAdjustments(adjustments);
      const batch = writeBatch(db);
      batch.update(doc(db, 'orders', order.id), {
        status: nextStatus,
        updatedAt: serverTimestamp(),
      });
      valueAdjustmentResult.entries.forEach((entry) => {
        const adjustmentRef = doc(collection(db, 'inventoryAdjustments'));
        batch.set(adjustmentRef, {
          ...entry,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });
      await batch.commit();
      if (unresolvedItemNames.length) {
        setError(
          `A státusz frissült, de ezekhez a tételekhez nem találtam raktár azonosítót: ${unresolvedItemNames.join(', ')}`,
        );
      } else {
        setError('');
      }
    } catch {
      updateOrderStatusInState(order, currentStatus);
      const rollbackDirection =
        currentStatus === 'new' && nextStatus === 'packed'
          ? -1
          : currentStatus === 'packed' && nextStatus === 'new'
            ? 1
            : 0;
      if (rollbackDirection !== 0) {
        const { adjustments } = createStockAdjustments(order, rollbackDirection);
        applyStockAdjustments(reverseAdjustments(adjustments));
      }
      setError('Nem sikerült frissíteni a rendelés státuszát.');
    } finally {
      setUpdatingOrderKeys((prev) => {
        const next = { ...prev };
        delete next[orderKey];
        return next;
      });
    }
  };

  const handleDeleteOrder = async (order) => {
    const orderLabel = order.orderNumber ? ` (${order.orderNumber})` : '';
    if (!window.confirm(`Biztosan törlöd ezt a rendelést${orderLabel}?`)) return;

    if (order.id) {
      try {
        await deleteDoc(doc(db, 'orders', order.id));
      } catch {
        setError('Nem sikerült törölni a rendelést.');
      }
      return;
    }

    setOrders((prev) => {
      const next = prev.filter((item) => item.orderNumber !== order.orderNumber);
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(ORDERS_STORAGE_KEY, JSON.stringify(next));
        window.dispatchEvent(new Event('lumenverde-orders-updated'));
      }
      return next;
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-zinga">
            <div className="rounded-3xl border border-[#0F3D2C] p-8 md:p-10 bg-card/60">
              <p className="text-sm text-muted-foreground">Betöltés...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-zinga">
            <h1 className="font-display text-3xl md:text-4xl font-bold mb-6">Admin / Rendelések</h1>
            <AdminLoginCard description="A rendelések megtekintéséhez jelentkezz be Google fiókkal." />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="section-padding">
        <div className="container-zinga">
          <AdminHeader title="Admin / Rendelések" />
          <AdminNav />

          <div className="mt-8 inline-flex flex-wrap gap-2 rounded-2xl border border-border bg-card p-2">
            <button
              type="button"
              onClick={() => setSearchParams({})}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
                activeView === 'orders'
                  ? 'bg-[#0F3D2C] text-[#F1E6D3]'
                  : 'text-foreground hover:bg-secondary'
              }`}
            >
              Rendelések
            </button>
            <button
              type="button"
              onClick={() => setSearchParams({ nez: 'felbehagyott' })}
              className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
                activeView === 'abandoned'
                  ? 'bg-[#0F3D2C] text-[#F1E6D3]'
                  : 'text-foreground hover:bg-secondary'
              }`}
            >
              Félbehagyott pénztárak
            </button>
          </div>

          {activeView === 'abandoned' ? (
            <div className="mt-6">
              <AbandonedCheckoutsPanel />
            </div>
          ) : (
          <div className="mt-6 space-y-4">
            {error && (
              <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                {error}
              </div>
            )}
            <div className="grid gap-6 lg:grid-cols-2">
              <section className="rounded-3xl border border-border/70 bg-card/60 p-4 overflow-hidden">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="font-display text-lg font-semibold">Szükséges termékek</h2>
                    <p className="text-xs text-muted-foreground mt-1">
                      Összesítés az összes új státuszú rendelés alapján.
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-muted-foreground">Új rendelések</p>
                    <p className="font-display text-xl font-semibold">{newOrders.length}</p>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-border/70 bg-card p-3">
                    <p className="text-xs text-muted-foreground">Terméktípus</p>
                    <p className="font-semibold">{aggregatedRequiredProducts.length}</p>
                  </div>
                  <div className="rounded-2xl border border-border/70 bg-card p-3">
                    <p className="text-xs text-muted-foreground">Összes darab</p>
                    <p className="font-semibold">{aggregatedQuantityTotal}</p>
                  </div>
                </div>

                {aggregatedRequiredProducts.length ? (
                  <div className="mt-4 rounded-2xl border border-border/70 bg-card overflow-hidden">
                    <div className="grid grid-cols-[minmax(0,1fr)_72px_72px_72px_28px] gap-2 px-3 py-2 text-xs text-muted-foreground border-b border-border/70">
                      <span>Termék</span>
                      <span className="text-right">Rendelés</span>
                      <span className="text-right">Raktár</span>
                      <span className="text-right">Úton</span>
                      <span className="text-right"> </span>
                    </div>
                    <div className="divide-y divide-border/60">
                      {aggregatedRequiredProducts.map((item) => (
                        <div
                          key={item.key}
                          className="grid grid-cols-[minmax(0,1fr)_72px_72px_72px_28px] gap-2 px-3 py-2 text-sm items-center"
                        >
                          <div className="min-w-0">
                            <p className="font-medium break-words">{item.name}</p>
                            {item.unitPrice !== null && (
                              <p className="text-xs text-muted-foreground">
                                Egységár: {item.unitPrice.toLocaleString('hu-HU')} Ft
                              </p>
                            )}
                          </div>
                          <span className="text-right font-semibold">{item.quantity}</span>
                          <span className="text-right">{item.stockQty}</span>
                          <span className="text-right">{item.inTransitQty}</span>
                          <span className="flex justify-end">
                            {item.availability === 'missing_stock' ? (
                              <XCircle
                                size={16}
                                className="text-red-500"
                                aria-label="A rendelési mennyiség nagyobb, mint a raktár és úton lévő mennyiség együtt."
                              />
                            ) : item.availability === 'warning' ? (
                              <AlertTriangle
                                size={16}
                                className="text-yellow-500"
                                aria-label="A rendelési mennyiség nagyobb, mint a raktárkészlet."
                              />
                            ) : (
                              <CheckCircle2
                                size={16}
                                className="text-emerald-600"
                                aria-label="A rendelési mennyiség belefér a raktárkészletbe."
                              />
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 rounded-2xl border border-border/70 bg-card p-6 text-sm text-muted-foreground">
                    Jelenleg nincs új státuszú rendelés, így termékigény sem számolható.
                  </div>
                )}
              </section>

              <section className="rounded-3xl border border-border/70 bg-card/60 p-4 overflow-hidden">
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <h2 className="font-display text-lg font-semibold">Rendelések</h2>
                    <p className="text-xs text-muted-foreground mt-1">
                      {filteredOrders.length} megjelenítve / {sortedOrders.length} összesen
                    </p>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:min-w-0">
                    <label className="block sm:flex-1 sm:min-w-[182px]">
                      <span className="text-xs text-muted-foreground">Szűrés állapot szerint</span>
                      <select
                        className="input-zinga mt-1 text-sm py-2"
                        value={statusFilter}
                        onChange={(event) => setStatusFilter(event.target.value)}
                      >
                        <option value="all">Összes állapot ({sortedOrders.length})</option>
                        {ORDER_STATUSES.map((status) => (
                          <option key={status.id} value={status.id}>
                            {status.label} ({statusCounts[status.id] || 0})
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="block sm:w-[154px] sm:max-w-full">
                      <span className="text-xs text-muted-foreground">Szűrés csomagküldő szerint</span>
                      <select
                        className="input-zinga mt-1 text-sm py-2"
                        value={carrierFilter}
                        onChange={(event) => setCarrierFilter(event.target.value)}
                      >
                        <option value="all">Összes ({carrierCounts.all})</option>
                        <option value="gls">GLS ({carrierCounts.gls})</option>
                        <option value="packeta">Packeta ({carrierCounts.packeta})</option>
                      </select>
                    </label>
                  </div>
                </div>

                {filteredOrders.length ? (
                  <div className="space-y-3">
                    {filteredOrders.map((order) => {
                      const orderStatus = getOrderStatus(order);
                      const shippingLabel = getShippingMethodLabel(order.shipping);
                      const shippingBadgeClassName = getShippingBadgeClassName(order.shipping);
                      const pickupPointLabel = formatPickupPointLabel(order.pickupPoint);
                      const hasPickupPoint = hasPickupPointShipping(order.shipping) && Boolean(pickupPointLabel);
                      const packValidation =
                        orderStatus === 'new'
                          ? getPackValidation(order)
                          : { canPack: true, unresolvedItemNames: [], insufficientProducts: [] };

                      return (
                        <details
                          key={order.id || order.orderNumber}
                          className="rounded-2xl border border-border/70 bg-card p-4"
                        >
                        <summary className="cursor-pointer grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
                          <div>
                            <p className="font-medium break-words">Rendelésszám: {order.orderNumber}</p>
                            <p className="text-xs text-muted-foreground break-words">
                              {formatDate(order.createdAt)}
                            </p>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <span className="inline-flex rounded-full border border-border/80 bg-muted/60 px-2 py-0.5 text-xs">
                                {getOrderStatusLabel(orderStatus)}
                              </span>
                              <span
                                className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${shippingBadgeClassName}`}
                              >
                                {shippingLabel}
                              </span>
                              {order.invoice?.status === 'issued' ? (
                                <span className="inline-flex rounded-full border border-emerald-300/80 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800">
                                  Számla: {order.invoice.number}
                                </span>
                              ) : order.invoice?.status === 'failed' ? (
                                <span className="inline-flex rounded-full border border-red-300/80 bg-red-50 px-2 py-0.5 text-xs text-red-800">
                                  Számlázási hiba
                                </span>
                              ) : order.invoice?.status === 'processing' ? (
                                <span className="inline-flex rounded-full border border-amber-300/80 bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                                  Számla készül…
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <div className="flex flex-col items-start sm:items-end gap-2">
                            <div className="text-sm font-medium whitespace-nowrap">
                              {order.total?.toLocaleString?.('hu-HU') ?? order.total} Ft
                            </div>
                            <select
                              className="input-zinga text-xs py-1 px-2 w-full sm:w-auto disabled:opacity-60"
                              value={orderStatus}
                              onChange={(event) => handleStatusChange(order, event.target.value)}
                              onClick={(event) => event.stopPropagation()}
                              onFocus={(event) => event.stopPropagation()}
                              disabled={Boolean(updatingOrderKeys[order.id || order.orderNumber])}
                            >
                              {ORDER_STATUSES.map((option) => (
                                <option
                                  key={option.id}
                                  value={option.id}
                                  disabled={
                                    option.id === 'packed' &&
                                    orderStatus === 'new' &&
                                    !packValidation.canPack
                                  }
                                >
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </summary>

                        {orderStatus === 'new' && !packValidation.canPack ? (
                          <p className="mt-3 text-xs text-destructive">
                            Csak akkor tehető összecsomagoltra, ha minden tételnél a raktárkészlet
                            legalább akkora, mint a rendelési darabszám.
                          </p>
                        ) : null}

                        <div className="mt-4">
                          <p className="text-xs text-muted-foreground mb-2">Tételek</p>
                          <div className="space-y-2 text-sm">
                            {(order.items || []).map((item, index) => (
                              <div key={`${order.orderNumber}-${index}`} className="flex justify-between gap-3">
                                <span className="break-words">
                                  {item.name} × {item.quantity}
                                </span>
                                <span className="shrink-0">
                                  {Number.isFinite(Number(item?.price) * Number(item?.quantity))
                                    ? `${(Number(item.price) * Number(item.quantity)).toLocaleString('hu-HU')} Ft`
                                    : '-'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="mt-4 text-sm">
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Vevő</p>
                            <p className="font-medium break-words">{order.customer?.name}</p>
                            <p className="break-words">{order.customer?.email}</p>
                            {order.customer?.phone && <p className="break-words">{order.customer.phone}</p>}
                            <p className="mt-2">
                              <span className="break-words">
                                {order.customer?.zip} {order.customer?.city}, {order.customer?.address}
                              </span>
                            </p>
                          </div>

                          <div className="mt-4 grid gap-6 md:grid-cols-2">
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Fizetés / Szállítás</p>
                              <p className="break-words">
                                Fizetés: {order.payment === 'card' ? 'Bankkártya' : 'Utánvét'}
                              </p>
                              <p className="break-words">
                                Szállítás: {shippingLabel}
                              </p>
                              {hasPickupPoint ? (
                                <p className="break-words text-xs text-muted-foreground mt-1">
                                  Csomagpont: {pickupPointLabel}
                                </p>
                              ) : null}
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Összesítés</p>
                              <p>Részösszeg: {order.subtotal?.toLocaleString?.('hu-HU') ?? order.subtotal} Ft</p>
                              <p>
                                Szállítási díj:{' '}
                                {order.shippingCost?.toLocaleString?.('hu-HU') ?? order.shippingCost} Ft
                              </p>
                            </div>
                          </div>
                        </div>

                        {order.invoice?.status === 'failed' ? (
                          <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                            <p className="font-medium">A számla nem készült el.</p>
                            <p className="mt-1 break-words">
                              {order.invoice.errorCode ? `${order.invoice.errorCode}: ` : ''}
                              {order.invoice.error || 'Ismeretlen Számlázz.hu hiba.'}
                            </p>
                          </div>
                        ) : null}

                        <div className="mt-4 flex flex-wrap justify-end gap-2">
                          {order.invoice?.status === 'issued' ? (
                            <button
                              type="button"
                              className="text-xs px-3 py-1.5 rounded-md border border-emerald-700/40 text-emerald-800 hover:bg-emerald-50 transition-colors disabled:opacity-60"
                              onClick={() => handleInvoiceDownload(order)}
                              disabled={downloadingInvoiceId === order.id}
                            >
                              {downloadingInvoiceId === order.id ? 'Megnyitás…' : 'Számla PDF'}
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="text-xs px-3 py-1.5 rounded-md border border-destructive/40 text-destructive hover:bg-destructive/10 transition-colors"
                            onClick={() => handleDeleteOrder(order)}
                          >
                            Rendelés törlése
                          </button>
                        </div>
                        </details>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-border/70 bg-card p-6 text-sm text-muted-foreground">
                    {sortedOrders.length === 0
                      ? 'Még nincs leadott rendelés.'
                      : 'Nincs rendelés a kiválasztott státuszban.'}
                  </div>
                )}
              </section>
            </div>
          </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
